import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ForecastVersion } from '../../types';

interface CreateVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: CreateVersionParams) => Promise<ForecastVersion>;
  existingVersions: ForecastVersion[];
  currentYear?: number;
}

export interface CreateVersionParams {
  name: string;
  type: 'Forecast' | 'Budget';
  baseVersionId?: number;
  copyOptions: {
    drivers: boolean;
    assumptions: boolean;
    overrides: boolean;
    notes: boolean;
  };
  year: number;
}

export function CreateVersionModal({
  isOpen,
  onClose,
  onSubmit,
  existingVersions,
  currentYear = new Date().getFullYear(),
}: CreateVersionModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'Forecast' | 'Budget'>('Forecast');
  // Default to starting from scratch — copying drivers from an existing version
  // silently skips the "Build with AI" setup flow on the new version.
  const [baseVersionId, setBaseVersionId] = useState<number | undefined>(undefined);
  const [copyOptions, setCopyOptions] = useState({
    drivers: true,
    assumptions: true,
    overrides: true,
    notes: true,
  });
  const [year, setYear] = useState(currentYear);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The modal stays mounted while the user changes the workspace year. Reset
  // its target whenever it opens so "Create Version" defaults to the year the
  // user is actually viewing instead of a stale year from the first render.
  useEffect(() => {
    if (isOpen) setYear(currentYear);
  }, [isOpen, currentYear]);

  // Generate year options
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a version name');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        type,
        baseVersionId,
        copyOptions,
        year,
      });
      toast.success('Version created successfully');
      handleClose();
    } catch (error) {
      const response = (error as { response?: { data?: { error?: string; details?: string; message?: string } } }).response?.data;
      const code = (error as { code?: string }).code;
      const uncertain = code === 'ECONNABORTED' || code === 'ETIMEDOUT' || code === 'ERR_NETWORK';
      toast.error(uncertain
        ? 'Creation status is unknown. The server may still finish. Refresh Versions before trying again to avoid a duplicate.'
        : response?.details || response?.message || response?.error || 'Failed to create version', { duration: uncertain ? 12000 : 5000 });
      console.error('Create version error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setName('');
    setType('Forecast');
    setBaseVersionId(undefined);
    setCopyOptions({
      drivers: true,
      assumptions: true,
      overrides: true,
      notes: true,
    });
    setYear(currentYear);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fv3-scrim absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fv3-dialog relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="fv3-dialog-title text-lg font-semibold text-slate-800">Create New Version</h2>
          <button
            onClick={handleClose}
            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Version Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Version Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={type === 'Budget' ? `e.g., ${year} Budget` : 'e.g., Feb 2026 Reforecast'}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
              autoFocus
              onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
              onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Type
            </label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="type"
                  value="Forecast"
                  checked={type === 'Forecast'}
                  onChange={() => setType('Forecast')}
                  className=""
                  style={{ accentColor: 'var(--lodge-orange-400)' }}
                />
                <span className="text-sm text-slate-700">Forecast</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="type"
                  value="Budget"
                  checked={type === 'Budget'}
                  onChange={() => setType('Budget')}
                  className=""
                  style={{ accentColor: 'var(--lodge-orange-400)' }}
                />
                <span className="text-sm text-slate-700">Budget</span>
              </label>
            </div>
          </div>

          {/* Year */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Fiscal Year
            </label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
              onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
              onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            {year !== currentYear && (
              <p className="text-xs mt-1.5" style={{ color: 'var(--lodge-orange-500)' }}>
                You're currently viewing {currentYear}. After creating, the workspace will switch to {year} to show this version.
              </p>
            )}
          </div>

          {/* Base Version — only show if there are versions to copy from */}
          {existingVersions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Copy From (Optional)
              </label>
              <select
                value={baseVersionId || ''}
                onChange={(e) => setBaseVersionId(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
                onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)'; e.currentTarget.style.borderColor = 'var(--lodge-orange-400)'; }}
                onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
              >
                <option value="">Start from scratch</option>
                {existingVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name} ({version.year} {version.type})
                  </option>
                ))}
              </select>
            </div>
          )}

          {type === 'Budget' && (
            <p className="text-xs text-slate-500">
              Budget drivers use {year - 1} budget amounts, room counts, and revenue shares.
              The editable column is Budget; add LY Budget and LY Budget $POR in Columns.
              Existing versions keep their saved calculation rules.
            </p>
          )}
          {baseVersionId && (
            <p className="text-xs text-slate-500">
              Copies drivers, assumptions, adjustments, overrides, daily entries, and their notes.
              Months move to {year}. Budget-based drivers stay linked to the published budget:
              later changes to its amounts, room counts, or revenue update their calculated rates.
              User-entered rate and value overrides carry over and continue to take precedence.
              Other history-based rates and PMS/RMS amounts retain their copy-time values;
              labor hourly rates are carried over. Clear an override to resume its source calculation.
              Dollar amounts can change because past forecast months use actual revenue while the future budget uses planned revenue. February 29 daily entries
              are omitted when the target year is not a leap year.
            </p>
          )}

          {/* Actions */}
          <div className="fv3-dialog-actions flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Version'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateVersionModal;
